import { useRequest } from '@features/request/useRequest';
import { useAgGridDataSource } from '@hooks/useAgGridDataSource';
import { destroyAgGridTableFilters } from '@utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { catalogRatiosHistoryItemsGql } from '../catalogRatiosHistoryQueries';
import type { TFilterState, TFilterStateType } from '../helpers';
import { getHistoryFilterValue, historyFilterColumnsFieldNames } from '../helpers';

import { useHistoryCounts } from './useHistoryCounts';

export const catalogRatiosHistoryRequestId = 'catalogRatiosHistoryItems';

export const useHistoryData = (catalogId: string | undefined) => {
    const tableRef = useRef<AgGridReact>(null);

    const [filterValue, setFilterValue] = useState<TFilterState | null>(null);

    const countsObj = useHistoryCounts({
        catalogUniqueId: catalogId,
        endDate: filterValue?.dateFilter?.[1]?.toISOString(),
        startDate: filterValue?.dateFilter?.[0]?.toISOString(),
    });

    const handlerFilterChange = useCallback(
        ({ filterType, value }: { filterType?: TFilterStateType; value?: TFilterState[TFilterStateType] } = {}) => {
            setFilterValue((prev) => {
                if (filterType === 'columnFilters') {
                    return {
                        ...prev,
                        [filterType]: {
                            ...prev?.[filterType],
                            ...(value as Record<string, any>),
                        },
                    } as TFilterState;
                }

                if (filterType) {
                    tableRef?.current?.api?.onFilterChanged();

                    let newValue = value;

                    if (filterType === 'dateFilter') {
                        if (Array.isArray(value)) {
                            newValue = value.map((date, idx) => {
                                if (idx === 1 && date) {
                                    const nextDayDate = new Date(date);

                                    nextDayDate.setDate(nextDayDate.getDate() + 1);

                                    return nextDayDate;
                                }

                                return date;
                            }) as [Date?, Date?];
                        }
                    }

                    return {
                        ...prev,
                        [filterType]: newValue,
                    } as TFilterState;
                }

                return prev;
            });
        },
        [],
    );

    useEffect(() => {
        if (!filterValue?.dateFilter) {
            tableRef?.current?.api?.showNoRowsOverlay();
        }
    }, [filterValue?.dateFilter]);

    const variables = useMemo(() => {
        if (catalogId && filterValue?.dateFilter) {
            return {
                catalogUniqueId: catalogId,
                where: getHistoryFilterValue(filterValue),
            };
        }

        return undefined;
    }, [catalogId, filterValue]);

    const [data, , , request] = useRequest({
        requestId: catalogRatiosHistoryRequestId,
        query: catalogRatiosHistoryItemsGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: catalogRatiosHistoryRequestId,
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (data?.catalogRatioHistoryItems?.items && getRowParams?.current) {
            const { items, totalCount } = data.catalogRatioHistoryItems;

            getRowParams?.current?.successCallback(items, totalCount);
        }
    }, [data?.catalogRatioHistoryItems, getRowParams]);

    const dateFilterValues = useMemo(
        () => ({
            startDate: filterValue?.dateFilter?.[0],
            endDate: filterValue?.dateFilter?.[1],
        }),
        [filterValue?.dateFilter],
    );

    useEffect(() => {
        if (data?.catalogRatioHistoryItems?.totalCount) {
            destroyAgGridTableFilters({
                api: tableRef?.current?.api,
                columns: historyFilterColumnsFieldNames,
            });
        }
    }, [data?.catalogRatioHistoryItems?.totalCount, dateFilterValues]);

    return {
        dataSource,
        handlerFilterChange,
        tableRef,
        countsObj,
        tableRdy: Boolean(variables),
        dateFilterValues,
    };
};
